import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import OverviewTab from '../tabs/OverviewTab';
import ConstellationTab from '../tabs/ConstellationTab';
import AnalyticsTab from '../tabs/AnalyticsTab';
import PassportTab from '../tabs/PassportTab';
import ArnsTab from '../tabs/ArnsTab';
import HistoryTab from '../tabs/HistoryTab';
import { StatusMessage } from '../../types';

// props interface
interface ProfileContentProps {
  activeTab: string;
  userData: any;
  currentAvatar: string | null;
  timeSeriesData: any;
  githubActivityTypes: any;
  xActivityTypes: any;
  setShowXPostsModal: (show: boolean) => void;
  canMintPassport: boolean;
  pointsNeeded: number;
  verifyAmPassport: () => void;
  verifyingPassport: boolean;
  passportError: string;
  linkX: () => void;
  linkGithub: () => void;
  linkDiscord: () => void;
  address: string;
  copiedField: string;
  setCopiedField: (field: string) => void;
  currentPrimaryName: string | null;
  userAntProcessId: string | null;
  primaryNameRequest: any;
  antOptions: any[];
  loadingAntCheck: boolean;
  antCheckError?: string;
  checkPrimaryNameAndAnt: () => void;
  antNameInput: string;
  setAntNameInput: (value: string) => void;
  selectedAntProcessId?: string;
  setSelectedAntProcessId: (value: string) => void;
  requestPrimaryName: () => Promise<void>;
  handleUpdateAntMetadata: () => void;
  updatingAnt: boolean;
  antUpdateStatus: any;
  undernameInput: string;
  setUndernameInput: (value: string) => void;
  handleSetUndername: () => void;
  settingUndername: boolean;
  undernameStatus: any;
  avatarFile: File | null;
  avatarPreview: string | null;
  handleAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploadingAvatar: boolean;
  handleUploadAvatar: () => void;
  avatarStatus: any;
  undernames: any;
  syncAntData: () => void;
  userHistory: any[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  // Add these new props
  level: number;
  levelTitle: string;
  setUpdatingAnt: React.Dispatch<React.SetStateAction<boolean>>;
  setAntUpdateStatus: React.Dispatch<React.SetStateAction<StatusMessage>>;
}

const ProfileContent: React.FC<ProfileContentProps> = ({ 
  activeTab,
  userData,
  currentAvatar,
  timeSeriesData,
  githubActivityTypes,
  xActivityTypes,
  setShowXPostsModal,
  canMintPassport,
  pointsNeeded,
  verifyAmPassport,
  verifyingPassport,
  passportError,
  linkX,
  linkGithub,
  linkDiscord,
  address,
  copiedField,
  setCopiedField,
  currentPrimaryName,
  userAntProcessId,
  primaryNameRequest,
  antOptions,
  loadingAntCheck,
  antCheckError,
  checkPrimaryNameAndAnt,
  antNameInput,
  setAntNameInput,
  selectedAntProcessId,
  setSelectedAntProcessId,
  requestPrimaryName,
  handleUpdateAntMetadata,
  updatingAnt,
  antUpdateStatus,
  undernameInput,
  setUndernameInput,
  handleSetUndername,
  settingUndername,
  undernameStatus,
  avatarFile,
  avatarPreview,
  handleAvatarChange,
  uploadingAvatar,
  handleUploadAvatar,
  avatarStatus,
  undernames,
  syncAntData,
  userHistory,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  level,
  levelTitle,
  setUpdatingAnt,
  setAntUpdateStatus
}) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="space-y-8 pb-8"
      >
        {activeTab === "overview" && (
          <OverviewTab 
            userData={userData}
            timeSeriesData={timeSeriesData}
            githubActivityTypes={githubActivityTypes}
            xActivityTypes={xActivityTypes}
            onShowXPosts={() => setShowXPostsModal(true)}
          />
        )}

        {activeTab === "constellation" && (
          <ConstellationTab 
            userData={userData}
            currentAvatar={currentAvatar}
          />
        )}

        {activeTab === "analytics" && (
          <AnalyticsTab 
            userData={userData}
            timeSeriesData={timeSeriesData}
            githubActivityTypes={githubActivityTypes}
            xActivityTypes={xActivityTypes}
            userHistory={userHistory}
          />
        )}

        {activeTab === "passport" && (
          <PassportTab 
            userData={userData}
            canMintPassport={canMintPassport}
            pointsNeeded={pointsNeeded}
            verifyAmPassport={verifyAmPassport}
            verifyingPassport={verifyingPassport}
            passportError={passportError}
            linkX={linkX}
            linkGithub={linkGithub}
            linkDiscord={linkDiscord}
            address={address}
            copiedField={copiedField}
            setCopiedField={setCopiedField}
          />
        )}

        {activeTab === "arns" && (
          <ArnsTab
            currentPrimaryName={currentPrimaryName}
            userAntProcessId={userAntProcessId}
            primaryNameRequest={primaryNameRequest}
            antOptions={antOptions}
            loadingAntCheck={loadingAntCheck}
            antCheckError={antCheckError}
            checkPrimaryNameAndAnt={checkPrimaryNameAndAnt}
            antNameInput={antNameInput}
            setAntNameInput={setAntNameInput}
            selectedAntProcessId={selectedAntProcessId}
            setSelectedAntProcessId={setSelectedAntProcessId}
            requestPrimaryName={requestPrimaryName}
            canMintPassport={canMintPassport}
            handleUpdateAntMetadata={handleUpdateAntMetadata}
            updatingAnt={updatingAnt}
            antUpdateStatus={antUpdateStatus}
            undernameInput={undernameInput}
            setUndernameInput={setUndernameInput}
            handleSetUndername={handleSetUndername}
            settingUndername={settingUndername}
            undernameStatus={undernameStatus}
            currentAvatar={currentAvatar}
            avatarFile={avatarFile}
            avatarPreview={avatarPreview}
            handleAvatarChange={handleAvatarChange}
            uploadingAvatar={uploadingAvatar}
            handleUploadAvatar={handleUploadAvatar}
            avatarStatus={avatarStatus}
            undernames={undernames}
            syncAntData={syncAntData}
            userData={userData}
            levelTitle={levelTitle}
            level={level}
            setUpdatingAnt={setUpdatingAnt}
            setAntUpdateStatus={setAntUpdateStatus}
          />
        )}

        {activeTab === "history" && (
          <HistoryTab 
            userHistory={userHistory}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfileContent;